import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Plus, Trash2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/format";

const STEPS = ["Income", "Essentials", "Living", "Safety", "Debts", "Review"];

const PAY_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "every_two_weeks", label: "Every two weeks" },
  { value: "twice_monthly", label: "Twice a month" },
  { value: "monthly", label: "Monthly" },
];

const ESSENTIAL_CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "utilities", label: "Utilities & Household" },
  { value: "food", label: "Food" },
  { value: "transportation", label: "Transportation" },
  { value: "insurance", label: "Insurance & Health" },
  { value: "family", label: "Family" },
  { value: "required_debt", label: "Required Debt Payments" },
  { value: "other", label: "Other Essential" },
];

const DEBT_TYPES = [
  { value: "credit_card", label: "Credit card" },
  { value: "personal_loan", label: "Personal loan" },
  { value: "medical", label: "Medical" },
  { value: "other", label: "Other" },
];

const uid = () => Math.random().toString(36).slice(2, 9);

const fieldCls =
  "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none focus:ring-2 focus:ring-[#2F6B57]";
const labelCls = "text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]";

function freqLabel(v) {
  return (PAY_FREQUENCIES.find((f) => f.value === v) || {}).label || v;
}

export default function FoundationSetup() {
  const { state, saveFoundation } = useAppState();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const f = state?.foundation;
  const [income, setIncome] = useState({ take_home_paycheck: "", pay_frequency: "monthly", next_payday: "" });
  const [essentials, setEssentials] = useState([]);
  const [living, setLiving] = useState("");
  const [safety, setSafety] = useState({ emergency_savings: "", other_savings: "" });
  const [debts, setDebts] = useState([]);

  // Prefill from an existing foundation (edit mode) / existing debts.
  useEffect(() => {
    if (!state) return;
    if (f) {
      setIncome({
        take_home_paycheck: String(f.income?.take_home_paycheck ?? ""),
        pay_frequency: f.income?.pay_frequency || "monthly",
        next_payday: f.income?.next_payday || "",
      });
      setEssentials((f.essentials || []).map((e) => ({ ...e, id: e.id || uid() })));
      setLiving(String(f.living_bucket ?? ""));
      setSafety({
        emergency_savings: String(f.safety?.emergency_savings ?? ""),
        other_savings: String(f.safety?.other_savings ?? ""),
      });
    }
    setDebts(
      (state.debts || []).map((d) => ({
        id: d.id,
        debt_type: d.debt_type || "credit_card",
        name: d.name,
        balance: String(d.balance ?? ""),
        minimum_payment: String(d.minimum_payment ?? ""),
        minimum_due_day: d.minimum_due_day != null ? String(d.minimum_due_day) : "",
        apr: d.apr != null ? String(d.apr) : "",
      }))
    );
  }, [state, f]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  if (!state) return null;

  const addEssential = () =>
    setEssentials((prev) => [...prev, { id: uid(), category: "housing", name: "", amount: "", frequency: "monthly", due_day: "", next_due_date: "" }]);
  const updateEssential = (id, patch) =>
    setEssentials((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeEssential = (id) => setEssentials((prev) => prev.filter((e) => e.id !== id));

  const addDebt = () =>
    setDebts((prev) => [...prev, { id: uid(), debt_type: "credit_card", name: "", balance: "", minimum_payment: "", minimum_due_day: "", apr: "" }]);
  const updateDebt = (id, patch) => setDebts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const removeDebt = (id) => setDebts((prev) => prev.filter((d) => d.id !== id));

  const num = (v) => parseFloat(v) || 0;
  const totalDebt = debts.reduce((s, d) => s + num(d.balance), 0);

  const canNext = () => {
    if (step === 0) return num(income.take_home_paycheck) > 0;
    return true;
  };

  const lockIn = async () => {
    setSaving(true);
    try {
      await saveFoundation({
        income: {
          type: "predictable",
          take_home_paycheck: num(income.take_home_paycheck),
          pay_frequency: income.pay_frequency,
          next_payday: income.next_payday || null,
        },
        essentials: essentials
          .filter((e) => e.name && num(e.amount) > 0)
          .map((e) => ({
            id: e.id, category: e.category, name: e.name, amount: num(e.amount), frequency: e.frequency,
            due_day: e.frequency === "monthly" && e.due_day ? parseInt(e.due_day, 10) : null,
            next_due_date: e.frequency !== "monthly" && e.next_due_date ? e.next_due_date : null,
          })),
        living_bucket: num(living),
        safety: {
          emergency_savings: num(safety.emergency_savings),
          other_savings: num(safety.other_savings),
        },
        debts: debts
          .filter((d) => d.name && num(d.balance) >= 0)
          .map((d) => ({
            id: state.debts.some((x) => x.id === d.id) ? d.id : undefined,
            debt_type: d.debt_type,
            name: d.name,
            balance: num(d.balance),
            minimum_payment: num(d.minimum_payment),
            minimum_due_day: d.minimum_due_day ? parseInt(d.minimum_due_day, 10) : null,
            apr: d.apr ? num(d.apr) : null,
          })),
        locked: true,
      });
      toast.success("Your foundation is locked in.");
      navigate("/app");
    } catch (e) {
      toast.error("Could not save your foundation. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up mx-auto max-w-2xl space-y-6" data-testid="foundation-setup-view">
      {/* Progress */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70584A]">
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </p>
        <div className="mt-2 flex gap-1.5" data-testid="foundation-progress">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#2F6B57]" : "bg-[#E5E0D9]"}`}
            />
          ))}
        </div>
      </div>

      {/* STEP: Income */}
      {step === 0 && (
        <div className="space-y-5" data-testid="foundation-step-income">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">How does your income usually work?</h1>
            <p className="mt-2 text-[#525252]">Set this up once. You won't re-enter it every payday.</p>
          </div>
          <div className="space-y-3">
            <div className="rounded-2xl border-2 border-[#2F6B57] bg-white p-4" data-testid="income-option-predictable">
              <p className="font-medium text-[#171717]">Predictable income</p>
              <p className="text-sm text-[#70584A]">My paycheck is usually about the same.</p>
            </div>
            <div className="rounded-2xl border border-[#E5E0D9] bg-[#F5F2EC] p-4 opacity-70">
              <p className="font-medium text-[#70584A]">Variable income <span className="text-xs">(Coming later)</span></p>
            </div>
            <div className="rounded-2xl border border-[#E5E0D9] bg-[#F5F2EC] p-4 opacity-70">
              <p className="font-medium text-[#70584A]">Hybrid income <span className="text-xs">(Coming later)</span></p>
            </div>
          </div>

          <div className="rounded-2xl pe-surface p-5 space-y-4">
            <div>
              <label className={labelCls}>Typical take-home paycheck</label>
              <input
                data-testid="income-paycheck-input"
                inputMode="decimal"
                value={income.take_home_paycheck}
                onChange={(e) => setIncome({ ...income, take_home_paycheck: e.target.value.replace(/[^0-9.]/g, "") })}
                placeholder="$0"
                className={fieldCls}
              />
            </div>
            <div>
              <label className={labelCls}>Pay frequency</label>
              <select
                data-testid="income-frequency-select"
                value={income.pay_frequency}
                onChange={(e) => setIncome({ ...income, pay_frequency: e.target.value })}
                className={fieldCls}
              >
                {PAY_FREQUENCIES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Next expected payday</label>
              <input
                data-testid="income-payday-input"
                type="date"
                value={income.next_payday}
                onChange={(e) => setIncome({ ...income, next_payday: e.target.value })}
                className={fieldCls}
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP: Essentials */}
      {step === 1 && (
        <div className="space-y-5" data-testid="foundation-step-essentials">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">What needs to be covered?</h1>
            <p className="mt-2 text-[#525252]">
              Add the expenses that need to be covered before your next Edit. Think must-pay expenses, not every little purchase.
            </p>
          </div>
          <div className="space-y-3">
            {essentials.map((e) => (
              <div key={e.id} className="rounded-2xl pe-surface p-4 space-y-3" data-testid="essential-row">
                <div className="flex items-center gap-2">
                  <input
                    value={e.name}
                    onChange={(ev) => updateEssential(e.id, { name: ev.target.value })}
                    placeholder="Name (e.g. Rent)"
                    className={fieldCls + " mt-0 flex-1"}
                  />
                  <button type="button" onClick={() => removeEssential(e.id)} className="rounded-lg p-2 text-[#991B1B] hover:bg-[#F5F2EC]" aria-label="Remove">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <select value={e.category} onChange={(ev) => updateEssential(e.id, { category: ev.target.value })} className={fieldCls + " mt-0"}>
                    {ESSENTIAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <input
                    inputMode="decimal"
                    value={e.amount}
                    onChange={(ev) => updateEssential(e.id, { amount: ev.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="$ Amount"
                    className={fieldCls + " mt-0"}
                  />
                  <select value={e.frequency} onChange={(ev) => updateEssential(e.id, { frequency: ev.target.value })} className={fieldCls + " mt-0"}>
                    {PAY_FREQUENCIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  {e.frequency === "monthly" ? (
                    <input
                      data-testid="essential-due-day"
                      inputMode="numeric"
                      value={e.due_day ?? ""}
                      onChange={(ev) => updateEssential(e.id, { due_day: ev.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                      placeholder="Due day of month (1–31), optional"
                      className={fieldCls + " mt-0"}
                    />
                  ) : (
                    <input
                      data-testid="essential-due-date"
                      type="date"
                      value={e.next_due_date ?? ""}
                      onChange={(ev) => updateEssential(e.id, { next_due_date: ev.target.value })}
                      className={fieldCls + " mt-0"}
                    />
                  )}
                  <p className="mt-1 text-xs text-[#A89A88]">Tell us when it's due so we only set it aside on the Edit it actually falls in. Leave blank and we'll play it safe.</p>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" data-testid="add-essential-button" onClick={addEssential} className="min-h-11 rounded-xl border-[#E5E0D9] bg-white text-[#164C3B] hover:bg-[#F5F2EC]">
            <Plus className="mr-1.5 h-4 w-4" /> Add an expense
          </Button>
        </div>
      )}

      {/* STEP: Living */}
      {step === 2 && (
        <div className="space-y-5" data-testid="foundation-step-living">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">Money for everyday life</h1>
            <p className="mt-2 text-[#525252]">
              You don't need to track every coffee, Target run or dinner out. Give yourself one amount for everyday spending.
            </p>
          </div>
          <div className="rounded-2xl pe-surface p-5">
            <label className={labelCls}>How much do you want available for everyday spending between paychecks?</label>
            <input
              data-testid="living-bucket-input"
              inputMode="decimal"
              value={living}
              onChange={(e) => setLiving(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="$0"
              className={fieldCls}
            />
          </div>
        </div>
      )}

      {/* STEP: Safety */}
      {step === 3 && (
        <div className="space-y-5" data-testid="foundation-step-safety">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">Your safety cushion</h1>
            <p className="mt-2 text-[#525252]">This money stays out of your Edit calculation.</p>
          </div>
          <div className="rounded-2xl pe-surface p-5 space-y-4">
            <div>
              <label className={labelCls}>Emergency savings</label>
              <input
                data-testid="safety-emergency-input"
                inputMode="decimal"
                value={safety.emergency_savings}
                onChange={(e) => setSafety({ ...safety, emergency_savings: e.target.value.replace(/[^0-9.]/g, "") })}
                placeholder="$0"
                className={fieldCls}
              />
            </div>
            <div>
              <label className={labelCls}>Other savings you want to keep protected</label>
              <input
                data-testid="safety-other-input"
                inputMode="decimal"
                value={safety.other_savings}
                onChange={(e) => setSafety({ ...safety, other_savings: e.target.value.replace(/[^0-9.]/g, "") })}
                placeholder="$0"
                className={fieldCls}
              />
            </div>
            <p className="text-xs text-[#70584A]">
              Don't include retirement accounts, home equity, credit availability or money you can't easily reach.
            </p>
          </div>
        </div>
      )}

      {/* STEP: Debts */}
      {step === 4 && (
        <div className="space-y-5" data-testid="foundation-step-debts">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">Your debts</h1>
            <p className="mt-2 text-[#525252]">
              {debts.length} {debts.length === 1 ? "debt" : "debts"} · {fmt(totalDebt)} total. We never ask for account numbers.
            </p>
          </div>
          <div className="space-y-3">
            {debts.map((d) => (
              <div key={d.id} className="rounded-2xl pe-surface p-4 space-y-3" data-testid="debt-row">
                <div className="flex items-center gap-2">
                  <input
                    value={d.name}
                    onChange={(ev) => updateDebt(d.id, { name: ev.target.value })}
                    placeholder="Nickname (e.g. Chase)"
                    className={fieldCls + " mt-0 flex-1"}
                  />
                  <button type="button" onClick={() => removeDebt(d.id)} className="rounded-lg p-2 text-[#991B1B] hover:bg-[#F5F2EC]" aria-label="Remove">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <select value={d.debt_type} onChange={(ev) => updateDebt(d.id, { debt_type: ev.target.value })} className={fieldCls + " mt-0"}>
                    {DEBT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input
                    inputMode="decimal"
                    value={d.balance}
                    onChange={(ev) => updateDebt(d.id, { balance: ev.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="$ Current balance"
                    className={fieldCls + " mt-0"}
                  />
                  <input
                    inputMode="decimal"
                    value={d.minimum_payment}
                    onChange={(ev) => updateDebt(d.id, { minimum_payment: ev.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="$ Minimum payment"
                    className={fieldCls + " mt-0"}
                  />
                  <input
                    data-testid="debt-min-due-day"
                    inputMode="numeric"
                    value={d.minimum_due_day ?? ""}
                    onChange={(ev) => updateDebt(d.id, { minimum_due_day: ev.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                    placeholder="Min due day (1–31)"
                    className={fieldCls + " mt-0"}
                  />
                  <input
                    inputMode="decimal"
                    value={d.apr}
                    onChange={(ev) => updateDebt(d.id, { apr: ev.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="APR % (optional)"
                    className={fieldCls + " mt-0"}
                  />
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" data-testid="add-debt-button" onClick={addDebt} className="min-h-11 rounded-xl border-[#E5E0D9] bg-white text-[#164C3B] hover:bg-[#F5F2EC]">
            <Plus className="mr-1.5 h-4 w-4" /> Add a debt
          </Button>
        </div>
      )}

      {/* STEP: Review */}
      {step === 5 && (
        <div className="space-y-5" data-testid="foundation-step-review">
          <div>
            <h1 className="font-serif text-3xl text-[#171717]">We've got your baseline. ✓</h1>
            <p className="mt-2 text-[#525252]">
              You won't have to enter all of this every payday. When money lands, we'll use these numbers to calculate what may be available for your next Edit.
            </p>
          </div>

          <div className="space-y-3">
            <ReviewRow label="Typical paycheck" value={`${fmt(num(income.take_home_paycheck))} · ${freqLabel(income.pay_frequency)}`} />
            <ReviewRow
              label="Reserved for essentials"
              value={fmt(essentials.filter((e) => num(e.amount) > 0).reduce((s, e) => s + num(e.amount), 0)) + " (before normalizing)"}
            />
            <ReviewRow label="Everyday spending" value={fmt(num(living))} />
            <ReviewRow label="Protected savings" value={fmt(num(safety.emergency_savings) + num(safety.other_savings))} />
            <ReviewRow label="Debts & minimums" value={`${debts.length} · ${fmt(debts.reduce((s, d) => s + num(d.minimum_payment), 0))}/mo min`} />
          </div>

          <div className="flex items-start gap-2.5 rounded-xl bg-[#DDE7DF] p-4 text-sm leading-relaxed text-[#164C3B]">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
            <span>Your protected savings stay out of every Edit calculation. The Paycheck Edit never moves your money.</span>
          </div>

          <Button
            data-testid="lock-foundation-button"
            onClick={lockIn}
            disabled={saving}
            className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"
          >
            {saving ? "Saving…" : "Lock in my foundation"}
            {!saving && <CheckCircle2 className="ml-1.5 h-4 w-4" />}
          </Button>
        </div>
      )}

      {/* Nav buttons */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          data-testid="foundation-back-button"
          onClick={() => (step === 0 ? navigate(-1) : setStep((s) => s - 1))}
          className="min-h-11 rounded-xl text-[#70584A] hover:bg-[#F5F2EC]"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        {step < STEPS.length - 1 && (
          <Button
            data-testid="foundation-next-button"
            onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Add your paycheck to continue")}
            className="min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]"
          >
            Next <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl pe-surface px-4 py-3">
      <span className="text-sm text-[#70584A]">{label}</span>
      <span className="font-medium text-[#171717]">{value}</span>
    </div>
  );
}
